import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LandingPageLayout from '@/Layout/LandingPageLayout';
import {
  cancelOrganizationSelection,
  getOrganizationSelectionInfo,
  selectOrganization,
  switchOrganizationSelectionAccount,
  type OrganizationSelectionInfo,
} from '@/apis/organization-selection';
import TextLink from '@/components/TextLink';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import Button from '@/shared/components/Button';
import LoadingLayer from '@/shared/components/LoadingLayer';

import styles from './index.module.scss';

const OrganizationSelection = () => {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const redirectTo = useGlobalRedirectTo();
  const getInfo = useApi(getOrganizationSelectionInfo);
  const select = useApi(selectOrganization);
  const cancel = useApi(cancelOrganizationSelection);
  const switchAccount = useApi(switchOrganizationSelectionAccount);
  const [data, setData] = useState<OrganizationSelectionInfo>();
  const [selected, setSelected] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [queryError, setQueryError] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(true);

  const load = useCallback(async () => {
    setLoadingInfo(true);
    setQueryError(false);
    const [error, result] = await getInfo();
    if (error) {
      setQueryError(true);
      setLoadingInfo(false);
      await handleError(error);
      return;
    }
    setData(result);
    setLoadingInfo(false);
    setSelected(undefined);
  }, [getInfo, handleError]);

  useEffect(() => {
    void load();
  }, [load]);

  const runAction = async (action: typeof cancel) => {
    if (loading) {
      return;
    }
    setLoading(true);
    try {
      const [error, result] = await action();
      if (error) {
        await handleError(error);
      } else if (result?.redirectTo) {
        await redirectTo(result.redirectTo);
      }
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (selected) {
      await runAction(async () => select(selected));
    }
  };
  if (!data && !queryError) {
    return <LoadingLayer />;
  }
  const applicationName = data?.application.displayName ?? data?.application.name ?? '';
  const organizations = data?.organizations ?? [];

  return (
    <LandingPageLayout
      title="description.organization_selection_title"
      titleInterpolation={{ name: applicationName }}
    >
      {data?.user && (
        <div className={styles.account}>
          {data.user.primaryEmail ?? data.user.username ?? data.user.id}
        </div>
      )}
      {queryError || organizations.length === 0 ? (
        <div className={styles.emptyState}>
          <p>
            {t(
              queryError
                ? 'description.organization_selection_load_error'
                : 'description.organization_selection_no_organizations'
            )}
          </p>
          <Button title="action.retry" onClick={async () => load()} />
        </div>
      ) : (
        <div className={styles.list}>
          {organizations.map((organization) => (
            <button
              key={organization.id}
              type="button"
              className={styles.item}
              data-selected={selected === organization.id}
              onClick={() => {
                setSelected(organization.id);
              }}
            >
              {organization.name || organization.id}
            </button>
          ))}
        </div>
      )}
      <div className={styles.footer}>
        <Button
          title="action.cancel"
          type="secondary"
          isLoading={loading}
          onClick={async () => runAction(cancel)}
        />
        {organizations.length > 0 && (
          <Button
            title="action.continue"
            isDisabled={!selected || loading || loadingInfo || queryError}
            isLoading={loading}
            onClick={async () => submit()}
          />
        )}
      </div>
      <div className={styles.switchAccount}>
        {t('description.not_you')}{' '}
        <TextLink
          text="action.use_another_account"
          onClick={async () => runAction(switchAccount)}
        />
      </div>
    </LandingPageLayout>
  );
};

export default OrganizationSelection;
